/**
 * The four-slot ceiling, with one law for every door that moves skin weight
 * onto a bone (the crease band, the muscle belly, the wrist re-own, the
 * pelvis re-own): DON'T MOVE WHAT YOU CANNOT SEAT.
 *
 * The defect this closes (Codex P2 on #108, then found in every sibling
 * door): a door adds its receiver as a FIFTH influence on a vertex that
 * already holds four, and a plain strongest-first sort evicts the weakest —
 * which, at the edge of any taper, is the receiver itself; the moved donor
 * weight is then renormalized into unrelated bones. The first cure tried
 * here — reserve the receiver's seat unconditionally — only moved the step
 * to the taper's rim (Codex P1 on #109): a receiver worth 0.05 evicted an
 * unrelated 0.15 the instant the taper began, a crease where the door meant
 * to remove one.
 *
 * With a hard cap, a fifth bone entering a full vertex can never be
 * continuous; the only choices are WHERE the step lands and WHAT moves
 * before it. Law: seats go by strength (lower slot index on ties, so the
 * result is deterministic). A receiver that would not be seated is reported
 * as UNSEATED, and the door leaves that vertex UNTOUCHED — the donor keeps
 * its weight, nothing is renormalized, the taper is continuous at its rim —
 * until the receiver's share STRICTLY outranks the weakest existing
 * influence, the crossover, where the swap is the smallest step any scheme
 * can make. A tie is unseated: quantized weights and fractional taper
 * positions produce exact ties, and a tie decided by slot index would make
 * the result depend on bone ORDER (Codex P2, #108). The door counts
 * unseated vertices; never silent.
 *
 * Depends on: nothing. Depended on by: every adapter weight door.
 */
export interface SeatedInfluences {
  /** The influences that keep a slot, strongest first — at most `ceiling` entries, all > 0. */
  readonly kept: ReadonlyArray<readonly [slot: number, weight: number]>;
  /** Everything that lost its seat, for the caller's accounting. */
  readonly evicted: ReadonlyArray<readonly [slot: number, weight: number]>;
  /** Receivers with positive weight that did not get a seat — the caller must not write this vertex. */
  readonly unseatedReceivers: readonly number[];
}

export function seatInfluences(
  weights: ReadonlyMap<number, number>,
  receivers: ReadonlySet<number>,
  ceiling: number,
): SeatedInfluences {
  if (!(ceiling >= 1) || !Number.isInteger(ceiling)) {
    throw new RangeError(`seatInfluences: ceiling must be a positive integer, got ${ceiling}`);
  }
  const live = [...weights.entries()]
    .filter(([, w]) => w > 0)
    .sort((a, b) => b[1] - a[1] || a[0] - b[0]);
  const kept = live.slice(0, ceiling);
  const evicted = live.slice(ceiling);
  // Strictly outrank: a seated receiver whose weight does not exceed the
  // strongest evicted influence only won its seat on slot order — unseated.
  const bar = evicted.length > 0 ? evicted[0]![1] : -Infinity;
  const unseatedReceivers = [
    ...kept.filter(([slot, w]) => receivers.has(slot) && w <= bar).map(([slot]) => slot),
    ...evicted.filter(([slot]) => receivers.has(slot)).map(([slot]) => slot),
  ];
  return { kept, evicted, unseatedReceivers };
}
